import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import PropTypes from 'prop-types';
import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import FormikInputField from 'commons/components/FormFields/Formik/FormikInputField';
import FormikPasswordField from 'commons/components/FormFields/Formik/FormikPasswordField';
import ConfigurationContext from 'UserRegistration/contexts/ConfigurationContext';
import DataContext from 'UserRegistration/contexts/DataContext';
import { isSaveButtonDisabled } from 'UserRegistration/contexts/configs/componentConfig';
import { populateUsername, updateContextValuesOnChange } from 'UserRegistration/resources/utils/personalInfoUtils';
import { pushRegistrationSavePersonalInformation } from 'UserRegistration/resources/utils/analytics';
import { saveSection } from 'UserRegistration/resources/utils/saveUtils';
import { validateSingleField, validateTouchedFieldList, formatBackendErrors, containsErrorType } from 'UserRegistration/resources/utils/ValidateUtils';
import { validateUsername, validateEmail, validatePassword } from 'UserRegistration/resources/api/validation';
import RegistrationSection from 'UserRegistration/components/RegistrationSteps/RegistrationSection';
import MetsMessages, { MetsMessagesSeverityEnum } from 'commons/components/MetsMessages';
import {validateUnifiedUserEmailFormat} from "../../../resources/api/validation";

function PublicPersonalInformation(props) {

    const { componentConfig } = useContext(ConfigurationContext);
    const { registrationData, setRegistrationData } = useContext(DataContext);
    const [ getShowUniqueUsernameErrorMessage, setShowUniqueUsernameErrorMessage ] = useState(false)
    const { t } = useTranslation(componentConfig.i18nNamespaces);

    const formikInitialValues = {
        userFirstName: "",
        userLastName: "",
        organizationName: "",
        phoneNumber: "",
        emailAddress: "",
        username: "",
        password: "",
        confirmPassword: "",
        termsAndConditionsAccepted: false
    };

    let yupValidationSchema = Yup.object({
        userFirstName: Yup.string()
            .trim()
            .required(t('error.required')),
        userLastName: Yup.string()
            .trim()
            .required(t('error.required')),
        organizationName: Yup.string()
            .trim()
            .required(t('error.required')),
        phoneNumber: Yup.string()
            .required(t('error.required'))
            .test(
                'Phone Number Not Blank',
                t('error.required'),
                (value) => value && value.trim().length !== 0
            )
            .matches('^[-0-9+(). ]*$', t('error.phoneNumber.invalid.characters')),
        password: Yup.string()
            .required(t('error.required'))
            .test('Backend Validation', "Backend Error", function(value) {
                if(value) {
                    return validatePassword(value)
                        .then((data) => {
                            if (data && data.length !== 0) {
                                let errorString = formatBackendErrors(data);
                                return (this.createError({ message: errorString }));
                            }
                            return true;
                        }).catch((e) => {
                            console.log(e);
                        });
                }
                return true;
            }),
        confirmPassword: Yup.string()
            .required(t('error.required'))
            .oneOf([Yup.ref('password')], t('error.passwords.must.match')),
        termsAndConditionsAccepted: Yup.boolean()
    });

    useEffect(() => {
        /* Don't execute during unit tests */
        if (!inTestMode()) {
            /* Set initial field values for an invited contact */
            props.formikRef?.current?.setFieldValue('userFirstName', registrationData.registrationModel.personalInformation.userFirstName ?? '', true);
            props.formikRef?.current?.setFieldValue('userLastName', registrationData.registrationModel.personalInformation.userLastName ?? '', true);
            props.formikRef?.current?.setFieldValue('organizationName', registrationData.registrationModel.personalInformation.organizationName ?? '', true);
            if (componentConfig.supplierEmailAsUsername) {
                props.formikRef?.current?.setFieldValue('emailUsername', registrationData.registrationModel.personalInformation.emailUsername ?? '', true);
            } else {
                props.formikRef?.current?.setFieldValue('emailAddress', registrationData.registrationModel.personalInformation.emailAddress ?? '', true);                
            }
        }     
    }, [registrationData.registrationModel.personalInformation.userFirstName,
        registrationData.registrationModel.personalInformation.userLastName,
        registrationData.registrationModel.personalInformation.organizationName, 
        registrationData.registrationModel.personalInformation.emailUsername, 
        registrationData.registrationModel.personalInformation.emailAddress]);

    if (componentConfig.supplierEmailAsUsername) {
        /**
         * MERX requires this call to the "validateUnifiedUserEmailFormat" function to validate the email format for
         * the combined email/username field. For SSC and other portals that will have this combined email/username,
         * the email format will technically be validated twice: once with "validateUnifiedUserEmailFormat"
         * and once with "validateUsername". Eventually this double validation should be removed once MERX
         * switches to use the same backend validator as SSC.
         */
        yupValidationSchema = yupValidationSchema.shape({
            emailUsername: Yup.string()
                .required(t('error.required'))
                .test('Backend Email Validation', "Backend Error", function (value) {
                    if (value) {
                        return validateUnifiedUserEmailFormat(value)
                            .then((data) => {
                                if (data && data.length !== 0) {
                                    let errorString = formatBackendErrors(data);
                                    return (this.createError({message: errorString}));
                                }
                                return true;
                            }).catch((e) => {
                                console.log(e);
                            });
                    }
                    return true;
                })
                .test('Backend Validation', "Backend Error", function (value) {
                    setShowUniqueUsernameErrorMessage(false);
                    if (value) {
                        return validateUsername(value)
                            .then((data) => {
                                if (data && data.length !== 0) {
                                    setShowUniqueUsernameErrorMessage(containsErrorType(data, "USERNAME_UNIQUE"));
                                    let errorString = formatBackendErrors(data);
                                    return (this.createError({message: errorString}));
                                }
                                return true;
                            }).catch((e) => {
                                console.log(e);
                            });
                    }
                    return true;
                })
        });
    } else {
        yupValidationSchema = yupValidationSchema.shape({
            emailAddress: Yup.string()
            .required(t('error.required'))
            .email(t('error.email.invalid'))
            .test('Backend Validation', 'Backend Error', function (value) {

                if (value) {

                    return validateEmail(value).then((data) => {

                        if (data) {
                            if (data.length !== 0) {
                                let errorString = formatBackendErrors(data);
                                return (this.createError({ message: errorString }));
                            } else if (!registrationData.registrationModel.personalInformation.username 
                                || value.includes(registrationData.registrationModel.personalInformation.username)) {
                                populateUsername(value, props.formikRef, registrationData, setRegistrationData);
                            }
                            return true;
                        }

                    });

                }

                return true;

            }),
            username: Yup.string()
                .required(t('error.required'))
                .test('Backend Validation', "Backend Error", function(value) {
                    setShowUniqueUsernameErrorMessage(false);
                    if(value) {
                        return validateUsername(value)
                            .then((data) => {
                                if (data && data.length !== 0) {
                                    setShowUniqueUsernameErrorMessage(containsErrorType(data, "USERNAME_UNIQUE"));
                                    let errorString = formatBackendErrors(data);
                                    return (this.createError({ message: errorString }));
                                }
                                return true;
                            }).catch((e) => {
                                console.log(e);
                            });
                    }
                    return true;
                })
        });
    }

    /**
     * 
     * @returns Determines if the code is being run by JEST (test architecture)
     */
    const inTestMode = () => {
        return process.env.NODE_ENV == 'test';
    }

    /**
     * Builds first 3 terms and conditions translations into a single line.
     * The second translation is made into a hyperlink for the terms and conditions proper.
     * Translations beyond 3 are each put into a new line.
     * 
     * @returns Array containing all the text pieces of the label.
     */
    const buildTermsAndConditionsLabelArray = () => {
        
        const labelArray = [];
        let labelIndex = 1;

        //If translation doesn't exist, return default empty string which is falsy and breaks the loop.
        while (t('terms.conditions.' + labelIndex, '')) {

            let labelFragment = t('terms.conditions.' + labelIndex);

            //Second translation is made into a hyperlink pointing to the actual terms and conditions.
            if (labelIndex === 2) {
                labelArray.push(
                    <a key="tsAndCsLink" id="tsAndCsLink" data-testid="termsAndConditionsLink" href="/cms-view.jsa?page=%2fcms%2fpublic%2fterms-conditions" title="Terms &amp; Conditions" className="mets-command-link" target="_blank">
                        {labelFragment}
                        <span className="accessibility-hidden">{' ' + t('opens.in.new.window')}</span>
                    </a>
                );
            }
            //Translations greater than 3 should be put into a new line. 
            else if (labelIndex > 3) {
                labelArray.push([
                    <br key={'tsAndCsMessageLineBreak' + labelIndex} />,
                    labelFragment
                ]);
            }
            //First and third translations should just be added as-is.
            else {
                labelArray.push(labelFragment);
            }

            labelIndex++;
        }

        return labelArray;
    };

    const formikOnSubmit = () => {
        saveSection(componentConfig, registrationData, setRegistrationData, pushRegistrationSavePersonalInformation);
    };

    return (
        
        <Formik
            initialValues={formikInitialValues}
            validationSchema={yupValidationSchema}
            onSubmit={formikOnSubmit}
            innerRef={props.formikRef}
            validateOnChange={false}
            validateOnBlur={false}>
                
            {formikProps => (

                <Form>

                    <RegistrationSection 
                        className="personal-info-section"
                        id="personalInfoSection"
                        saveButtonDisabled={isSaveButtonDisabled(registrationData, componentConfig)}
                        saveButtonOnClick={formikProps.submitForm}
                        sectionNumber={props.sectionNumber}
                        title={t('personalInformation.public')}>

                        <div className="section-fields">
                            <Field component={FormikInputField} 
                                    label={t('firstName')} name="userFirstName" 
                                    onChange={(e) => updateContextValuesOnChange(e, registrationData, setRegistrationData)} 
                                    autoComplete="new-password" maxLength={50}
                                    onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)}
                                     />
                            
                            <Field component={FormikInputField} 
                                label={t('lastName')} name="userLastName" 
                                onChange={(e) => updateContextValuesOnChange(e, registrationData, setRegistrationData)} 
                                autoComplete="new-password" maxLength={50}
                                onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)} />
                            
                            <Field component={FormikInputField} 
                                label={t('companyName')} name="organizationName" 
                                onChange={(e) => updateContextValuesOnChange(e, registrationData, setRegistrationData)} 
                                autoComplete="new-password" maxLength={80}
                                onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)} />
                            
                            <Field component={FormikInputField} label={t('phoneNumber')} name="phoneNumber" onChange={(e) => updateContextValuesOnChange(e, registrationData, setRegistrationData)} autoComplete="new-password" maxLength={20}
                                onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)} />
                        </div>

                        <h3 className="sub-section-title">
                            {t('connection.details')}
                        </h3>

                        <div className="section-fields">
                            {componentConfig.supplierEmailAsUsername 
                            ?  <Field component={FormikInputField} 
                                label={t('emailUsername')} name="emailUsername" 
                                rowClassName="large-field" 
                                onChange={(e) => updateContextValuesOnChange(e, registrationData, setRegistrationData)} 
                                autoComplete="new-password" maxLength={40}
                                onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)} />
                            : <>
                                    <Field component={FormikInputField} label={t('emailAddress')} name="emailAddress" onChange={(e) => updateContextValuesOnChange(e, registrationData, setRegistrationData)} autoComplete="new-password" maxLength={40}
                                        onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)} />
                                    <Field component={FormikInputField} label={t('username')} name="username" onChange={(e) => updateContextValuesOnChange(e, registrationData, setRegistrationData)} autoComplete="new-password" maxLength={40}
                                        onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)} />
                                </>
                            }

                            {getShowUniqueUsernameErrorMessage ?
                                <MetsMessages severity={MetsMessagesSeverityEnum.Info} containerClassName="unavailable-username-info-container">
                                    {t('error.username.login.1')}
                                    <ul>
                                        <li><a href="/public/authentication/login">{t('login')}</a></li>
                                        <li><a href="/public/authentication/password-recovery">{t('resetPassword')}</a></li>
                                        <li>{t('error.username.login.2', {phoneNumber: componentConfig.supportPhonenumber})}</li>
                                    </ul>
                                </MetsMessages>
                            : ''}
                            <Field component={FormikPasswordField} label={t('password')} name="password" onChange={(e) => updateContextValuesOnChange(e, registrationData, setRegistrationData)} autoComplete="new-password" maxLength={100}
                                description={t('passwordRules')}
                                onBlur={(e) => {
                                    validateSingleField(e.target.name, props.formikRef.current);
                                    validateTouchedFieldList(['confirmPassword'], props.formikRef.current)
                                }} />

                            <Field component={FormikPasswordField} label={t('confirmPassword')} name="confirmPassword" onChange={(e) => updateContextValuesOnChange(e, registrationData, setRegistrationData)} autoComplete="new-password" maxLength={100}
                                onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)} />

                            <Field component={FormikInputField} name="termsAndConditionsAccepted" type="checkbox" onChange={(e) => updateContextValuesOnChange(e, registrationData, setRegistrationData)}
                                onBlur={(e) => validateSingleField(e.target.name, props.formikRef.current)}
                                label={buildTermsAndConditionsLabelArray()} />

                        </div>
                    

                    </RegistrationSection>

                </Form>
        
            )}

        </Formik> 
            
    );
}

PublicPersonalInformation.propTypes = {
    /** A reference to the Formik component. */
    formikRef: PropTypes.any,
    /** The total number of configurable sections currently loaded in the instance of UserRegistration. */
    sectionCount: PropTypes.number,
    /** The position of this section in relation to other sections in the entire instance of UserRegistration. */
    sectionNumber: PropTypes.number,
    /** Available VAS Items to be displayed */
    availableVasItems: PropTypes.any
}

PublicPersonalInformation.defaultProps = {
    availableVasItems : []
}

export default PublicPersonalInformation;
